import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Lock, Search, Plus, Filter, FileText, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useQuery } from '@tanstack/react-query';

export default function FinanceChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [mutations, setMutations] = useState([]);
  const [activeBankFilter, setActiveBankFilter] = useState('Semua');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  const navigate = useNavigate();

  const { accountsMap } = useBankAccounts();

  const { clientPOs = [] } = useClientPOs();
  const { data: purchasingRequests = [] } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list(),
  });

  // Modals state
  const [isCreateAccountOpen, setIsCreateAccountOpen] = useState(false);
  const [isCreateJournalOpen, setIsCreateJournalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form State for Create Account
  const [newAccount, setNewAccount] = useState({ id: '', name: '', category: 'Cash & Bank' });

  // Form State for Journal
  const [journalData, setJournalData] = useState({
    bukti: 'Auto',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    bank_account: '',
    lines: [
      { accountId: '', debit: '', kredit: '' },
      { accountId: '', debit: '', kredit: '' }
    ]
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await base44.entities.ChartOfAccount.list('id');
      setAccounts(data || []);

      const allMutations = await base44.entities.AccountMutation.list();
      setMutations(allMutations);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const accountBalances = useMemo(() => {
    const balances = {};
    
    mutations.forEach(mut => {
      if (activeBankFilter !== 'Semua') {
        if (mut.account !== activeBankFilter) return;
      }
      
      if (mut.coa_allocations && Array.isArray(mut.coa_allocations)) {
        mut.coa_allocations.forEach(alloc => {
          if (alloc.account_id) {
            const accountId = alloc.account_id;
            const prefix = accountId.charAt(0);
            const isDebitNormal = ['1', '5', '6', '8'].includes(prefix);
            
            const allocAmount = Number(alloc.amount) || 0;
            let debit = 0;
            let credit = 0;
            
            if (mut.type === 'jurnal') {
              if (alloc.is_credit) {
                credit = allocAmount;
              } else {
                debit = allocAmount;
              }
            } else if (mut.type === 'keluar') {
              debit = allocAmount;
            } else if (mut.type === 'masuk') {
              credit = allocAmount;
            } else {
              debit = allocAmount;
            }
            
            // JANGAN terapkan perhitungan mutasi ke akun Bank secara manual di sini, 
            // karena kita akan paksa nilainya mengikuti saldo final dari useBankAccounts
            const bankCoaIds = ['1-10002', '1-10003', '1-10004'];
            if (bankCoaIds.includes(accountId)) return; 
            
            if (!balances[accountId]) balances[accountId] = 0;
            
            if (isDebitNormal) {
              balances[accountId] += (debit - credit);
            } else {
              balances[accountId] += (credit - debit);
            }
          }
        });
      }
    });

    // Kalkulasi final saldo Rekening Bank meniru persis halaman Mutasi
    // termasuk Saldo Awal (startBalance)
    const finalBankBalances = {
      bca: Number(accountsMap.bca?.startBalance) || 0,
      mandiri: Number(accountsMap.mandiri?.startBalance) || 0,
      bni: Number(accountsMap.bni?.startBalance) || 0
    };

    mutations.forEach(m => {
      const name = (m.account || '').toLowerCase().trim();
      let key = 'bca';
      if (name === (accountsMap.bca?.name || '').toLowerCase().trim()) key = 'bca';
      else if (name === (accountsMap.mandiri?.name || '').toLowerCase().trim()) key = 'mandiri';
      else if (name === (accountsMap.bni?.name || '').toLowerCase().trim()) key = 'bni';
      else if (name.includes('bca') || name.includes('saja')) key = 'bca';
      else if (name.includes('mandiri') || name.includes('cuan')) key = 'mandiri';
      else if (name.includes('bni') || name.includes('essential')) key = 'bni';

      const amt = Number(m.amount) || 0;
      if (m.type === 'masuk') {
        finalBankBalances[key] += amt;
      } else if (m.type === 'keluar') {
        finalBankBalances[key] -= amt;
      }
    });

    // Kalkulasi Real-Time Piutang & Hutang dari sumber asli (Opsi A)
    const activeKey = activeBankFilter !== 'Semua' ? Object.keys(accountsMap).find(k => accountsMap[k].name === activeBankFilter) || '' : '';
    const activeNameLower = activeBankFilter.toLowerCase();

    // Paksa COA Rekening Bank mengikuti saldo mutasi yang sebenarnya
    balances['1-10002'] = activeBankFilter === 'Semua' || activeKey === 'bca' ? finalBankBalances.bca : 0; // Rekening ASB
    balances['1-10003'] = activeBankFilter === 'Semua' || activeKey === 'mandiri' ? finalBankBalances.mandiri : 0; // Rekening CJT
    balances['1-10004'] = activeBankFilter === 'Semua' || activeKey === 'bni' ? finalBankBalances.bni : 0; // Rekening SDE

    // Piutang
    let totalPiutangUsaha = 0;
    let totalPiutangLain = 0;
    clientPOs.forEach(po => {
      // DO NOT SKIP LUNAS. Mengikuti murni hitungan invoice (Invoice Total - Paid)
      let pm = (po.paymentMethod || po.rawRecord?.paymentMethod || '').toLowerCase();
      let shouldInclude = true;
      if (activeBankFilter !== 'Semua') {
         shouldInclude = (activeKey && pm.includes(activeKey)) || pm.includes(activeNameLower) || pm === activeKey;
      }
      if (!shouldInclude) return;
      
      let t = 0;
      const items = po.products || po.items || [];
      items.forEach(p => t += (p.total_harga_jual || p.subtotal || p.hargaTotal || 0));
      
      let leg = 0;
      (po.legalitas || []).forEach(l => leg += (l.harga_jual || 0) * (l.qty_variant || 1));
      
      let paid = 0;
      (po.riwayatPembayaran || []).forEach(r => paid += (Number(r.jumlah) || 0));
      
      const shipping = Number(po.shippingCost || 0);
      const ppnMode = po.ppn_mode || po.rawRecord?.ppn_mode;
      const ppnPercent = parseFloat(po.ppn_percentage || po.rawRecord?.ppn_percentage) || 0;
      const nominalPPN = ppnMode === 'exclude' ? ((t + leg) * ppnPercent / 100) : 0;
      const invoiceTotal = t + leg + shipping + nominalPPN;
      
      const outstanding = invoiceTotal - paid;
      if (outstanding !== 0) {
        const createdDate = new Date(po.created_date || po.date || Date.now());
        const isTempo = po.rawRecord?.is_tempo || po.rawRecord?.paymentTerms === 'tempo' || po.statusBayar === 'TEMPO';
        let isOverdue = false;
        
        if (isTempo && po.rawRecord?.tempoDate) {
           const tempoDate = new Date(po.rawRecord.tempoDate);
           if (tempoDate < new Date()) {
             isOverdue = true;
           }
        } else {
           const diffTime = Math.abs(new Date() - createdDate);
           const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
           if (diffDays > 40) isOverdue = true;
        }

        if (isOverdue) {
          totalPiutangLain += outstanding;
        } else {
          totalPiutangUsaha += outstanding;
        }
      }
    });
    balances['1-10100'] = (balances['1-10100'] || 0) + totalPiutangUsaha;
    balances['1-10102'] = (balances['1-10102'] || 0) + totalPiutangLain;
    balances['4-40100'] = (balances['4-40100'] || 0) + (totalPiutangUsaha + totalPiutangLain);

    // Hutang
    // Hutang
    let totalHutangUsaha = 0;
    let totalHutangBelumDitagih = 0;
    purchasingRequests.forEach(pr => {
      if (pr.metode_pembayaran === 'Tempo' && pr.status_pembayaran_tempo === 'Menunggu Jatuh Tempo') {
        let shouldInclude = true;
        if (activeBankFilter !== 'Semua') {
           if (activeKey === 'bni') {
             // SDE
             shouldInclude = pr.ada_faktur_pajak === 'Ya';
           } else if (activeKey === 'bca') {
             // ASB
             shouldInclude = pr.ada_faktur_pajak !== 'Ya';
           } else {
             // Mandiri or others, exclude from Payables since they are paid via ASB/SDE
             shouldInclude = false;
           }
        }
        if (shouldInclude) {
          let isOverdue = false;
          if (pr.jatuh_tempo) {
            const tempoDate = new Date(pr.jatuh_tempo);
            if (tempoDate < new Date()) {
              isOverdue = true;
            }
          }
          if (isOverdue) {
            totalHutangBelumDitagih += Number(pr.total_biaya || 0);
          } else {
            totalHutangUsaha += Number(pr.total_biaya || 0);
          }
        }
      }
    });
    balances['2-20100'] = (balances['2-20100'] || 0) + totalHutangUsaha;
    balances['2-20101'] = (balances['2-20101'] || 0) + totalHutangBelumDitagih;

    return balances;
  }, [mutations, activeBankFilter, clientPOs, purchasingRequests]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!newAccount.id || !newAccount.name) return;
    
    setIsSaving(true);
    try {
      await base44.entities.ChartOfAccount.create({
        id: newAccount.id,
        name: newAccount.name,
        category: newAccount.category
      });
      await fetchData();
      setIsCreateAccountOpen(false);
      setNewAccount({ id: '', name: '', category: 'Cash & Bank' });
    } catch (err) {
      console.error("Gagal membuat akun:", err);
      alert("Gagal membuat akun");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateJournal = async (e) => {
    e.preventDefault();
    
    // Validasi keseimbangan Debit & Kredit
    let totalDebit = 0;
    let totalKredit = 0;
    const allocations = [];

    for (const line of journalData.lines) {
      if (!line.accountId) continue;
      const d = Number(line.debit) || 0;
      const k = Number(line.kredit) || 0;
      if (d === 0 && k === 0) continue;
      
      totalDebit += d;
      totalKredit += k;
      
      allocations.push({
        prefix: line.accountId.charAt(0),
        account_id: line.accountId,
        amount: d > 0 ? d.toString() : k.toString(),
        is_credit: k > 0
      });
    }

    if (allocations.length < 2) {
      alert("Jurnal harus memiliki minimal 2 baris akun.");
      return;
    }
    if (totalDebit !== totalKredit) {
      alert(`Jurnal tidak seimbang! Debit: ${totalDebit}, Kredit: ${totalKredit}`);
      return;
    }
    if (!journalData.bank_account) {
      alert("Pilih Cabang / Rekening Bank terlebih dahulu.");
      return;
    }

    setIsSaving(true);
    try {
      await base44.entities.AccountMutation.create({
        date: journalData.date,
        type: 'jurnal',
        account: journalData.bank_account,
        amount: totalDebit,
        description: journalData.notes || 'Jurnal Umum',
        reference_id: `jurnal-${Date.now()}`,
        status: 'Selesai',
        coa_allocations: allocations
      });
      
      alert("Jurnal Umum berhasil dicatat!");
      setIsCreateJournalOpen(false);
      setJournalData({
        bukti: 'Auto',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        bank_account: '',
        lines: [
          { accountId: '', debit: '', kredit: '' },
          { accountId: '', debit: '', kredit: '' }
        ]
      });
      fetchData();
    } catch (err) {
      console.error(err);
      alert("Gagal menyimpan jurnal");
    } finally {
      setIsSaving(false);
    }
  };

  const updateJournalLine = (index, field, value) => {
    const newLines = [...journalData.lines];
    newLines[index][field] = value;
    
    // Auto-disable opposite field
    if (field === 'debit' && value !== '') newLines[index].kredit = '';
    if (field === 'kredit' && value !== '') newLines[index].debit = '';
    
    setJournalData({ ...journalData, lines: newLines });
  };

  const addJournalLine = () => {
    setJournalData({
      ...journalData,
      lines: [...journalData.lines, { accountId: '', debit: '', kredit: '' }]
    });
  };

  const removeJournalLine = (index) => {
    const newLines = journalData.lines.filter((_, i) => i !== index);
    setJournalData({ ...journalData, lines: newLines });
  };

  const toggleExpand = (e, id) => {
    e.stopPropagation();
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filteredAccounts = accounts.filter(acc => 
    acc.id.toLowerCase().includes(search.toLowerCase()) || 
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.category.toLowerCase().includes(search.toLowerCase())
  );

  const accountOptions = accounts.map(a => ({ value: a.id, label: `${a.id} - ${a.name}` }));

  return (
    <div className="space-y-6 animate-in fade-in zoom-in duration-500">
      {/* Header */}
      <div className="bg-[#FFFDF9] border border-[#E6D5B8]/60 rounded-2xl p-6 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#F9F5EC] to-transparent opacity-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div>
            <h1 className="text-2xl font-black text-[#302621] tracking-tight">Daftar Akun</h1>
            <p className="text-sm text-[#7A695B] mt-1 font-medium">Chart of Accounts (COA)</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setIsCreateJournalOpen(true)} className="bg-[#302621] hover:bg-[#4A3D36] text-white shadow-md">
              <Plus className="w-4 h-4 mr-2" /> Buat Jurnal Umum
            </Button>
            <Button onClick={() => setIsCreateAccountOpen(true)} className="bg-[#8C6208] hover:bg-[#A37B24] text-white shadow-md">
              <Plus className="w-4 h-4 mr-2" /> Buat Akun Baru
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-[#FFFDF9] border border-[#E6D5B8]/60 rounded-2xl shadow-sm overflow-hidden flex flex-col">
        
        {/* Tab Sub Menu for Bank Account */}
        <div className="bg-slate-50 border-b border-slate-100 p-2 flex overflow-x-auto">
          <div className="flex bg-slate-200/50 p-1 rounded-xl">
            <button
              onClick={() => setActiveBankFilter('Semua')}
              className={`px-4 py-2 text-xs font-bold rounded-lg transition-all whitespace-nowrap ${activeBankFilter === 'Semua' ? 'bg-white text-[#302621] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Semua Rekening (Gabungan)
            </button>
            {Object.keys(accountsMap).map(k => (
              <button
                key={k}
                onClick={() => setActiveBankFilter(accountsMap[k].name)}
                className={`px-4 py-2 text-xs font-bold rounded-lg transition-all whitespace-nowrap ${activeBankFilter === accountsMap[k].name ? 'bg-white text-[#302621] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              >
                {accountsMap[k].name}
              </button>
            ))}
          </div>
        </div>

        {/* Toolbar */}
        <div className="p-4 border-b border-[#E6D5B8]/40 flex flex-col md:flex-row gap-4 justify-between items-center bg-[#F9F5EC]/30">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Cari kode atau nama akun..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] transition-all"
            />
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <Button variant="outline" className="w-full md:w-auto border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC]" onClick={() => alert("Fitur filter tingkat lanjut akan segera hadir")}>
              <Filter className="w-4 h-4 mr-2" /> Filter
            </Button>
            <Button variant="outline" className="w-full md:w-auto border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC]">
              Tindakan <span className="ml-2">▼</span>
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#F9F5EC]/80 border-b border-[#E6D5B8]/60 text-[#7A695B]">
              <tr>
                <th className="px-6 py-4 font-bold w-12 text-center">Kunci</th>
                <th className="px-6 py-4 font-bold">Kode Akun</th>
                <th className="px-6 py-4 font-bold">Nama Akun</th>
                <th className="px-6 py-4 font-bold">Kategori Akun</th>
                <th className="px-6 py-4 font-bold text-right">Saldo (dalam IDR)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6D5B8]/30">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-6 h-6 border-2 border-[#8C6208] border-t-transparent rounded-full animate-spin mb-2"></div>
                      <span className="text-sm">Memuat daftar akun...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                    <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                    <p>Tidak ada akun yang ditemukan</p>
                  </td>
                </tr>
              ) : (
                filteredAccounts
                  .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                  .map((acc) => (
                  <tr 
                    key={acc.id} 
                    onClick={(e) => {
                      const activeKey = activeBankFilter !== 'Semua' ? Object.keys(accountsMap).find(k => accountsMap[k].name === activeBankFilter) || '' : '';
                      const query = activeKey ? `?bank=${activeKey}` : '';
                      navigate(`/office/finance/ledger/${encodeURIComponent(acc.id)}${query}`);
                    }}
                    className="hover:bg-[#F9F5EC]/50 cursor-pointer transition-colors group"
                  >
                    <td className="px-6 py-3 text-center">
                      {['1-10100', '1-10200', '1-10402', '1-10500', '2-20100'].includes(acc.id) ? (
                        <div 
                          className="flex justify-center items-center gap-1 text-slate-400 cursor-pointer hover:text-[#8C6208]"
                          onClick={(e) => toggleExpand(e, acc.id)}
                        >
                          <Lock className="w-3.5 h-3.5" />
                          <span className="text-xs">{expandedRows[acc.id] ? '-' : '+'}</span>
                        </div>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-6 py-3 font-mono text-[#302621]">{acc.id}</td>
                    <td className="px-6 py-3 font-medium transition-colors">
                      {acc.id === '2-20100' ? (
                        <span 
                           className="text-[#8C6208] group-hover:text-[#A37B24] flex items-center gap-1.5 w-max cursor-pointer"
                           onClick={(e) => {
                              e.stopPropagation();
                              navigate('/office/finance/payables');
                           }}
                           title="Lihat Rincian Hutang"
                        >
                          {acc.name} <ExternalLink className="w-3.5 h-3.5 text-[#8C6208]/70" />
                        </span>
                      ) : (
                        <span className="text-[#8C6208] group-hover:text-[#A37B24]">{acc.name}</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-[#7A695B]">{acc.category}</td>
                    <td className="px-6 py-3 text-right font-mono font-medium text-[#302621]">
                      {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(accountBalances[acc.id] || 0)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-[#E6D5B8]/40 flex items-center justify-between bg-white rounded-b-2xl">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-500">Row per Page</span>
            <Select 
              value={String(rowsPerPage)} 
              onValueChange={(val) => {
                setRowsPerPage(Number(val));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[72px] h-8 text-sm font-semibold bg-white border-[#E6D5B8] rounded-full focus:ring-1 focus:ring-[#8C6208]/30">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F9F5EC] disabled:opacity-50 text-[#7A695B] font-medium transition-colors"
              >
                &lt;
              </button>
              
              {(() => {
                const totalPages = Math.ceil(filteredAccounts.length / rowsPerPage) || 1;
                const pages = [];
                if (totalPages <= 5) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  if (currentPage <= 3) {
                    pages.push(1, 2, 3, 4, '...', totalPages);
                  } else if (currentPage >= totalPages - 2) {
                    pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                  } else {
                    pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                  }
                }
                
                return pages.map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-[#7A695B] font-medium">...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                        currentPage === page 
                          ? 'bg-[#8C6208] text-white shadow-md' 
                          : 'text-[#302621] hover:bg-[#F9F5EC]'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ));
              })()}

              <button 
                onClick={() => setCurrentPage(Math.min(Math.ceil(filteredAccounts.length / rowsPerPage), currentPage + 1))}
                disabled={currentPage >= Math.ceil(filteredAccounts.length / rowsPerPage)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F9F5EC] disabled:opacity-50 text-[#7A695B] font-medium transition-colors"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Buat Akun Baru */}
      <Dialog open={isCreateAccountOpen} onOpenChange={setIsCreateAccountOpen}>
        <DialogContent className="sm:max-w-[425px] bg-[#FFFDF9] border-[#E6D5B8]">
          <DialogHeader>
            <DialogTitle className="text-[#302621]">Buat Akun Baru</DialogTitle>
            <DialogDescription className="text-[#7A695B]">
              Tambahkan akun baru ke dalam bagan akun (Chart of Accounts).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAccount} className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#302621]">Kategori Akun</label>
              <select 
                value={newAccount.category}
                onChange={(e) => setNewAccount({...newAccount, category: e.target.value})}
                className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 bg-white text-[#302621]"
                required
              >
                <option value="Cash & Bank">Cash & Bank</option>
                <option value="Accounts Receivable (A/R)">Accounts Receivable (A/R)</option>
                <option value="Other Current Asset">Other Current Asset</option>
                <option value="Fixed Asset">Fixed Asset</option>
                <option value="Depreciation">Depreciation</option>
                <option value="Accounts Payable (A/P)">Accounts Payable (A/P)</option>
                <option value="Current Liability">Current Liability</option>
                <option value="Long Term Liability">Long Term Liability</option>
                <option value="Equity">Equity</option>
                <option value="Income">Income</option>
                <option value="Cost of Sales">Cost of Sales</option>
                <option value="Expense">Expense</option>
                <option value="Other Expense">Other Expense</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#302621]">Kode Akun</label>
              <input 
                type="text" 
                placeholder="Contoh: 1-10010" 
                value={newAccount.id}
                onChange={(e) => setNewAccount({...newAccount, id: e.target.value})}
                className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 text-[#302621]"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#302621]">Nama Akun</label>
              <input 
                type="text" 
                placeholder="Contoh: Kas Kecil" 
                value={newAccount.name}
                onChange={(e) => setNewAccount({...newAccount, name: e.target.value})}
                className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 text-[#302621]"
                required
              />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCreateAccountOpen(false)} className="border-[#E6D5B8] text-[#7A695B]">
                Batal
              </Button>
              <Button type="submit" disabled={isSaving} className="bg-[#8C6208] hover:bg-[#A37B24] text-white">
                {isSaving ? 'Menyimpan...' : 'Simpan Akun'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Buat Jurnal Umum */}
      <Dialog open={isCreateJournalOpen} onOpenChange={setIsCreateJournalOpen}>
        <DialogContent className="sm:max-w-[800px] bg-[#FFFDF9] border-[#E6D5B8]">
          <DialogHeader>
            <DialogTitle className="text-[#302621] text-xl font-bold">Buat Jurnal Umum</DialogTitle>
            <DialogDescription className="text-[#7A695B]">
              Catat transaksi penyesuaian secara manual menggunakan double-entry (Debit & Kredit).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateJournal} className="space-y-5 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#302621]">Rekening Pencatatan</label>
                <select
                  value={journalData.bank_account}
                  onChange={(e) => setJournalData({...journalData, bank_account: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg text-[#302621] focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] outline-none transition-all"
                  required
                >
                  <option value="" disabled>Pilih Rekening Bank</option>
                  {Object.keys(accountsMap).map(k => (
                    <option key={k} value={accountsMap[k].name}>{accountsMap[k].name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#302621]">Nomor Bukti</label>
                <input 
                  type="text" 
                  value={journalData.bukti}
                  onChange={(e) => setJournalData({...journalData, bukti: e.target.value})}
                  placeholder="Auto" 
                  className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg text-[#302621] focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] outline-none transition-all" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#302621]">Tanggal Transaksi</label>
                <input 
                  type="date" 
                  value={journalData.date}
                  onChange={(e) => setJournalData({...journalData, date: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] transition-all text-[#302621]" 
                  required 
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#302621]">Keterangan</label>
              <textarea 
                placeholder="Penjelasan transaksi..." 
                rows={2} 
                value={journalData.notes}
                onChange={(e) => setJournalData({...journalData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] transition-all text-[#302621]" 
                required 
              />
            </div>
            
            <div className="border border-[#E6D5B8]/60 rounded-xl mt-4">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#F9F5EC] border-b border-[#E6D5B8]/60 text-[#302621]">
                  <tr>
                    <th className="px-4 py-3 font-semibold rounded-tl-xl">Akun</th>
                    <th className="px-4 py-3 font-semibold w-40 text-right">Debit (IDR)</th>
                    <th className="px-4 py-3 font-semibold w-40 text-right">Kredit (IDR)</th>
                    <th className="px-2 py-3 w-10 rounded-tr-xl"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E6D5B8]/30 bg-white">
                  {journalData.lines.map((line, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2">
                        <SearchableCombobox 
                          options={accountOptions}
                          value={line.accountId}
                          onValueChange={(val) => updateJournalLine(index, 'accountId', val)}
                          placeholder="Cari & pilih akun..."
                          className="w-full bg-transparent border-transparent hover:border-[#E6D5B8] shadow-none hover:shadow-sm"
                          allowCustom={false}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input 
                          type="number" 
                          placeholder="0" 
                          value={line.debit}
                          onChange={(e) => updateJournalLine(index, 'debit', e.target.value)}
                          disabled={line.kredit !== ''}
                          className="w-full text-right outline-none text-sm p-2 rounded hover:bg-slate-50 focus:bg-white focus:ring-1 focus:ring-[#8C6208] transition-all disabled:opacity-30 disabled:bg-slate-100" 
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input 
                          type="number" 
                          placeholder="0" 
                          value={line.kredit}
                          onChange={(e) => updateJournalLine(index, 'kredit', e.target.value)}
                          disabled={line.debit !== ''}
                          className="w-full text-right outline-none text-sm p-2 rounded hover:bg-slate-50 focus:bg-white focus:ring-1 focus:ring-[#8C6208] transition-all disabled:opacity-30 disabled:bg-slate-100" 
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {journalData.lines.length > 2 && (
                          <button 
                            type="button" 
                            onClick={() => removeJournalLine(index)}
                            className="text-red-400 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="bg-[#F9F5EC]/50 p-3 border-t border-[#E6D5B8]/40">
                <button 
                  type="button" 
                  onClick={addJournalLine}
                  className="text-sm text-[#8C6208] font-semibold hover:underline flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Tambah Baris
                </button>
              </div>
            </div>

            <DialogFooter className="pt-4 mt-6 border-t border-[#E6D5B8]/40">
              <Button type="button" variant="outline" onClick={() => setIsCreateJournalOpen(false)} className="border-[#E6D5B8] text-[#7A695B]">
                Batal
              </Button>
              <Button type="submit" disabled={isSaving} className="bg-[#302621] hover:bg-[#4A3D36] text-white font-semibold">
                {isSaving ? 'Menyimpan...' : 'Simpan Jurnal'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
